import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { COMMERCIAL_SITE } from '@connexio/shared/constants';
import { PublicMarketingShell } from '@/components/public/PublicMarketingShell';
import { Text } from '@/components/ui/text';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useLocale } from '@/hooks/useLocale';
import { MarketingFooter } from './MarketingFooter';
import styles from './LegalDocument.module.css';

const ENTITY = COMMERCIAL_SITE.legalEntity;

const LINK_CLASS =
  styles.link;

export interface LegalSectionSpec {
  id: string;
  paragraphs: number;
}

interface LegalDocumentProps {
  ns: 'legal' | 'privacy';
  path: '/terms' | '/privacy';
  sections: readonly LegalSectionSpec[];
}

export function LegalDocument({ ns, path, sections }: LegalDocumentProps) {
  const { t } = useTranslation(ns);
  const { t: tMarketing } = useTranslation('marketing');
  const { locale } = useLocale();
  const canonical = `${COMMERCIAL_SITE.canonicalOrigin}${path}`;

  const values = {
    entityName: ENTITY.name,
    orgNumber: ENTITY.orgNumber,
    address: ENTITY.address,
    email: COMMERCIAL_SITE.contactEmail,
    districtCourt: ns === 'legal' ? t('districtCourt') : '',
  };

  const linkComponents = {
    email: <a href={`mailto:${COMMERCIAL_SITE.contactEmail}`} className={LINK_CLASS} />,
    form: <Link to="/contact" className={LINK_CLASS} />,
    terms: <Link to="/terms" className={LINK_CLASS} />,
    privacy: <Link to="/privacy" className={LINK_CLASS} />,
    stripe: (
      <a href="https://stripe.com/privacy" className={LINK_CLASS} rel="noreferrer" target="_blank" />
    ),
    google: (
      <a
        href="https://developers.google.com/terms/api-services-user-data-policy"
        className={LINK_CLASS}
        rel="noreferrer"
        target="_blank"
      />
    ),
    imy: <a href="https://www.imy.se/" className={LINK_CLASS} rel="noreferrer" target="_blank" />,
  };

  useDocumentMeta({
    title: t('metaTitle'),
    description: t('metaDescription'),
    canonicalPath: canonical,
    lang: locale,
  });

  return (
    <PublicMarketingShell
      brandTo="/"
      brandTagline={tMarketing('nav.brandTagline')}
      sections={[]}
      footer={<MarketingFooter />}
      inlineNav
      fullBleed
      surfaceClassName="marketing-surface"
    >
      <article className={styles.article}>
        <h1 className={styles.title}>
          {t('title')}
        </h1>
        <Text textStyle="caption" tagName="p" className={styles.updated}>
          {t('updated')}
        </Text>
        <Text textStyle="body.l" tagName="p" className={styles.text}>
          <Trans i18nKey="intro" ns={ns} values={values} components={linkComponents} />
        </Text>
        <Text textStyle="body.l" tagName="p" className={styles.text2}>
          <Trans i18nKey="bind" ns={ns} values={values} components={linkComponents} />
        </Text>

        {sections.map((section) => (
          <section key={section.id} className={styles.section}>
            <h2 className={styles.heading}>
              {t(`sections.${section.id}.title`)}
            </h2>
            {Array.from({ length: section.paragraphs }, (_, index) => {
              const i18nKey = `sections.${section.id}.p${index + 1}`;
              return (
                <Text key={i18nKey} textStyle="body.l" tagName="p" className={styles.updated}>
                  <Trans
                    i18nKey={i18nKey}
                    ns={ns}
                    values={values}
                    components={linkComponents}
                  />
                </Text>
              );
            })}
          </section>
        ))}
      </article>
    </PublicMarketingShell>
  );
}
